import Table, {ColumnsType} from 'antd/lib/table';
import {Chart} from '@antv/g2';
import {FC, useEffect, useRef, useState} from 'react';
import {Col, DatePicker, Row, Select} from 'antd';
import {LOCATIONS, LOCATIONS_COLORS} from './Stats';
import moment from 'moment';
import {useG2Chart} from './hooks';

const {RangePicker} = DatePicker;

export interface DailyActivityData {
  family: string;
  indicator: string;
  location: string | null;
  number: number | null;
  amount: number | null;
  date: string;
}

const getChartData = (data: DailyActivityData[]): DailyActivityData[] => {
  return data.filter((obj) => obj.location !== null);
};

const createColumnChart = (
  container: HTMLDivElement,
  data: DailyActivityData[],
  param: 'amount' | 'number',
) => {
  const chart = new Chart({
    container,
    width: 1000,
    height: 500,
  });
  chart.data(getChartData(data));
  chart.coordinate().transpose().scale(1, -1);

  chart.axis(param, {
    position: 'right',
  });
  chart.axis('indicator', {
    label: {
      offset: 12,
    },
  });

  chart.tooltip({
    shared: true,
    showMarkers: false,
  });

  chart
    .interval()
    .position(`indicator*${param}`)
    .color('location', (location) => LOCATIONS_COLORS[location])
    .adjust([
      {
        type: 'dodge',
        marginRatio: 0,
      },
    ]);
  chart.render();

  return chart;
};

const useColumnChart = (
  ref: React.RefObject<HTMLDivElement>,
  label: 'amount' | 'number',
) => {
  const [chartData, setChartData] = useState<DailyActivityData[]>([]);
  const [chart, setChart] = useState<any>();

  useG2Chart(ref, chartData, (container, data) =>
    createColumnChart(container, data, label),
  );

  return {barChart: chart, setChartData};
};

export const DailyActivity: FC<{initialData: DailyActivityData[]}> = ({
  initialData,
}) => {
  const ref = useRef(null);
  const [data, setData] = useState<DailyActivityData[]>(initialData);
  const [dateRange, setDateRange] = useState<[string, string]>([
    '2024-01-01',
    '2024-03-31',
  ]);
  const [label, setLabel] = useState<'amount' | 'number'>('number');
  const [cities, setCities] = useState<string[]>([]);

  const {setChartData} = useColumnChart(ref, label);

  useEffect(() => {
    const filteredData = initialData.filter((obj) => {
      const dateFilter =
        moment(obj.date).isAfter(moment(dateRange[0])) &&
        moment(obj.date).isBefore(moment(dateRange[1]));
      if (cities.length === 0) {
        return dateFilter;
      }
      return (
        obj.location !== null && cities.includes(obj.location) && dateFilter
      );
    });

    setData(filteredData);
    setChartData(filteredData);
  }, [initialData, cities, setChartData, dateRange]);

  const columns: ColumnsType<DailyActivityData> = [
    {title: 'Famille', key: 'family', dataIndex: 'family'},
    {title: 'Indicateur', key: 'indicator', dataIndex: 'indicator'},
    {title: 'Site', key: 'location', dataIndex: 'location'},
    {title: 'Nombre', key: 'number', dataIndex: 'number'},
    {
      title: 'Montant',
      key: 'Amount',
      dataIndex: 'amount',
      render: (value: number) => (value !== null ? value.toString() + '€' : ''),
    },
  ];

  return (
    <>
      <Row style={{marginBottom: 10}} gutter={10}>
        <Col>
          <RangePicker
            onChange={(values) => {
              if (values)
                setDateRange([
                  values[0]?.format('YYYY-MM-DD') ?? '2024-01-01',
                  values[1]?.format('YYYY-MM-DD') ?? '2024-03-31',
                ]);
            }}
            value={[moment(dateRange[0]), moment(dateRange[1])]}
            format={'DD/MM/YYYY'}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            style={{width: 120}}
            placeholder="Choisir une ville"
            options={[
              {label: LOCATIONS.LYON, value: LOCATIONS.LYON},
              {label: LOCATIONS.NIMES, value: LOCATIONS.NIMES},
            ]}
            onChange={(value: string[]) => setCities(value)}
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={data}
        size="small"
        onRow={(record) =>
          record.location
            ? {style: {backgroundColor: LOCATIONS_COLORS[record.location]}}
            : {}
        }
      />
      <Row>
        <Select
          defaultValue="number"
          options={[
            {value: 'number', label: 'Nombre '},
            {value: 'amount', label: 'Montant'},
          ]}
          onChange={(value: 'number' | 'amount') => {
            setLabel(value);
          }}
        />
      </Row>
      <div ref={ref}></div>
    </>
  );
};
